import { useMutation } from "@tanstack/react-query";
import type { ProjectView, SetProjectParticipantsRequest } from "@portal/schemas";
import {
  Button,
  Checkbox,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@portal/ui";
import { useState } from "react";
import { setProjectParticipants } from "../../api/client";
import { useInvalidate, useMe, useProjectSuggestions } from "../../api/queries";
import { DialogTitleWithHint, Hint, LabelWithHint } from "../../components/Hint";
import { HomeSection } from "../../components/HomeSection";
import { InlineError } from "../../components/InlineError";
import { PARTICIPANT_SOURCE_LABELS } from "./ProjectStatusBadge";
import { Pencil, Plus, Save, Trash2 } from "lucide-react";

type Row = SetProjectParticipantsRequest["participants"][number];

export function ProjectParticipantsCard({ project }: { project: ProjectView }) {
  const me = useMe();
  const isSystemAdmin = me.data?.user.roles.includes("SYSTEM_ADMIN") ?? false;
  const active = project.participants.filter((m) => m.active);
  return (
    <HomeSection
      data-testid="project-participants-card"
      title={
        <>
          参加者
          <span className="ml-2 text-sm font-normal text-muted-foreground">{active.length} 名</span>
        </>
      }
      action={(project.canEdit || isSystemAdmin) && <EditDialog project={project} />}
    >
      <ul className="flex flex-col divide-y text-sm">
        {active.map((m) => (
          <li
            key={`${m.source}:${m.email ?? m.externalId}`}
            className="flex items-center justify-between py-2"
          >
            <span>
              {m.displayName}
              {m.isAdmin && <span className="ml-2 text-xs text-muted-foreground">管理者</span>}
            </span>
            <span className="text-xs text-muted-foreground">
              {PARTICIPANT_SOURCE_LABELS[m.source]}
            </span>
          </li>
        ))}
      </ul>
      <ParticipantSuggestions project={project} canEdit={project.canEdit || isSystemAdmin} />
    </HomeSection>
  );
}

/**
 * Backlog の受注後の課題(作成者・担当者・メンション先)から推定した参加者候補(ユーザー決定 2026-09-06)。
 * 一覧の下に出し、ポータルユーザーに照合できた人だけ追加ボタンを出す。
 */
function ParticipantSuggestions({ project, canEdit }: { project: ProjectView; canEdit: boolean }) {
  const suggestions = useProjectSuggestions(project.id);
  const invalidate = useInvalidate();
  const add = useMutation({
    // メールが分からない人も、表示名(と Backlog のユーザー ID)で追加できる
    // (ユーザー要望 2026-09-17)
    mutationFn: (person: { email?: string; displayName: string; externalId?: string }) =>
      setProjectParticipants(project.id, {
        revision: project.revision,
        participants: [
          ...manualRows(project).filter((r) =>
            person.email ? r.email !== person.email : r.displayName !== person.displayName,
          ),
          { ...person, isAdmin: false, active: true },
        ],
      }),
    onSuccess: async () => {
      await Promise.all([invalidate.projects(), suggestions.refetch()]);
    },
  });
  const rows = suggestions.data?.participants ?? [];
  if (suggestions.isPending || suggestions.isError || rows.length === 0) return null;
  return (
    <section aria-label="参加者のサジェスト" className="mt-4" data-testid="participant-suggestions">
      <h3 className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        サジェスト
        <Hint label="参加者のサジェストの説明">
          Backlog の受注後の課題から推定しました。確認のうえ追加してください。
        </Hint>
      </h3>
      <ul className="flex flex-col divide-y text-sm">
        {rows.map((s) => (
          <li
            key={s.backlogUserId || s.name}
            className="flex items-center justify-between gap-2 py-2"
          >
            <span className="flex min-w-0 flex-col">
              <span>
                {s.portalDisplayName ?? s.name}
                {!s.portalUserId && (
                  <span className="ml-2 text-xs text-muted-foreground">メール不明</span>
                )}
              </span>
              <span className="text-xs text-muted-foreground">{s.reasons.join("、")}</span>
            </span>
            {canEdit && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`${s.portalDisplayName ?? s.name} を参加者に追加`}
                title="追加"
                disabled={add.isPending}
                onClick={() =>
                  add.mutate({ email: s.portalUserId!, displayName: s.portalDisplayName ?? s.name })
                }
              >
                <Plus />
              </Button>
            )}
          </li>
        ))}
      </ul>
      <InlineError error={add.error} />
    </section>
  );
}

function manualRows(project: ProjectView): Row[] {
  return project.participants
    .filter((m) => m.source === "MANUAL")
    .map((m) => ({
      ...(m.email ? { email: m.email } : {}),
      ...(m.externalId ? { externalId: m.externalId } : {}),
      displayName: m.displayName,
      isAdmin: m.isAdmin,
      active: m.active,
    }));
}

/** 行を見分けるキー。メール不明の人は表示名で見分ける。 */
const rowKey = (r: Row) => r.email ?? r.externalId ?? r.displayName;

function EditDialog({ project }: { project: ProjectView }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>(() => manualRows(project));
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const invalidate = useInvalidate();
  const save = useMutation({
    mutationFn: () =>
      setProjectParticipants(project.id, {
        revision: project.revision,
        participants: rows.filter((r) => r.active),
      }),
    onSuccess: async () => {
      await invalidate.projects();
      setOpen(false);
    },
  });
  // ユーザーマスタを持たないので、メールアドレスで追加する(SPEC §2.8)
  // メールが分からない人は表示名だけで追加できる(ユーザー要望 2026-09-17)。
  // ただし管理者にはできない(権限の判定はメールで行うため)
  const addRow = () => {
    const nextEmail = email.trim().toLowerCase();
    const nextName = displayName.trim();
    if (nextEmail === "" && nextName === "") return;
    const row: Row = {
      ...(nextEmail ? { email: nextEmail } : {}),
      displayName: nextName || nextEmail,
      isAdmin: false,
      active: true,
    };
    if (rows.some((r) => rowKey(r) === rowKey(row))) return;
    setRows((rs) => [...rs, row]);
    setEmail("");
    setDisplayName("");
  };
  const setAdmin = (target: string, on: boolean) =>
    setRows((rs) => rs.map((r) => (rowKey(r) === target ? { ...r, isAdmin: on } : r)));
  const removeRow = (target: string) => setRows((rs) => rs.filter((r) => rowKey(r) !== target));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setRows(manualRows(project));
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="編集" title="編集">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitleWithHint hint="手動で追加した参加者だけを変更できます。Backlog などから見つけた参加者は、そのまま残ります。">
            参加者を編集
          </DialogTitleWithHint>
        </DialogHeader>
        <div className="flex flex-col gap-1 text-sm">
          {rows.length === 0 && (
            <p className="text-muted-foreground">手動で追加した参加者はいません。</p>
          )}
          {rows.map((r) => (
            <div
              key={rowKey(r)}
              className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-accent"
            >
              <span className="flex min-w-0 flex-col">
                <span>{r.displayName}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {r.email ?? "メール不明"}
                </span>
              </span>
              <span className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox
                    aria-label={`${r.displayName} を管理者にする`}
                    checked={r.isAdmin}
                    disabled={!r.email}
                    onCheckedChange={(v) => setAdmin(rowKey(r), v === true)}
                  />
                  管理者
                </label>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`${r.displayName} を外す`}
                  onClick={() => removeRow(rowKey(r))}
                >
                  <Trash2 />
                </Button>
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1.5">
            <LabelWithHint
              htmlFor="participant-email"
              hint="@zenk.co.jp のアドレスで追加します。分からなければ表示名だけでも追加できますが、管理者にはできません。"
            >
              メールアドレス(任意)
            </LabelWithHint>
            <Input
              id="participant-email"
              type="email"
              placeholder="t_suzuki@zenk.co.jp"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="participant-name">表示名</Label>
            <Input
              id="participant-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={addRow}
            disabled={email.trim() === "" && displayName.trim() === ""}
          >
            <Plus />
            追加
          </Button>
        </div>
        <InlineError error={save.error} />
        <DialogFooter>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            <Save />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
